<template>
  <div v-if="problem && problem.id" class="problem-details-section">
    <h2 class="basic-sub-title problem-sheet__section-title">
      {{ $t('message.DETAILS') }}
    </h2>

    <div class="problem-sheet__table">
      <div class="problem-sheet__row">
        <div class="problem-sheet__cell">
          <div class="problem-sheet__cell__wrapper problem-details-section__impact">
            <div class="legend legend--bottom-margin">
              {{ $t('message.IMPACT') }}
            </div>
            <ImpactSelector v-model="problem.impact" min @input="editProblem('impact')" />
            <div v-if="impactHint !== null" class="field-hint problem-details-section__impact__hint">
              {{ impactHint }}
            </div>
          </div>
        </div>
        <div class="problem-sheet__cell">
          <div class="problem-sheet__cell__wrapper">
            <TreeField
              v-if="typeof problem.types !== 'undefined'"
              v-model="problem.types"
              :legend="$t('message.TYPE')"
              :items="allTypes"
              children-key="types"
              name-key="name"
              :disabled="disabled"
              :placeholder="$t('message.CLICK_TO_CHANGE')"
              multiple
              @okClick="editProblem('types')"
            />
          </div>
        </div>
        <div class="problem-sheet__cell">
          <div class="problem-sheet__cell__wrapper">
            <TreeField
              v-if="typeof problem.zones !== 'undefined'"
              v-model="problem.zones"
              :legend="$t('message.LOCATION')"
              :items="allZones"
              :disabled="disabled"
              :placeholder="$t('message.CLICK_TO_CHANGE')"
              multiple
              @okClick="editProblem('zones')"
            />
          </div>
        </div>
        <div class="problem-sheet__cell">
          <div class="problem-sheet__cell__wrapper">
            <EditableLabelRadio
              v-model="problem.indicators"
              :legend="$t('message.INDICATORS')"
              :items="allIndicators"
              label-key="name"
              :disabled="disabled"
              :placeholder="$t('message.CLICK_TO_CHANGE')"
              multiple
              @input="editProblem('indicators')"
            />
          </div>
        </div>
      </div>
      <div class="problem-sheet__row">
        <div class="problem-sheet__cell">
          <div class="problem-sheet__cell__wrapper">
            <EditableLabel
              v-model="problem.editableKeywords"
              :legend="$t('message.KEYWORDS')"
              :autocomplete="allKeywords"
              :autocomplete-chips-type="AutocompleteChipsTypes.KEYWORD"
              :disabled="disabled"
              :placeholder="$t('message.CLICK_TO_TYPE')"
              keywords
              @input="editProblem('keywords')"
            />
          </div>
        </div>
        <div class="problem-sheet__cell" @click="isMounted ? $refs.pbDate.$el.click() : ''">
          <div class="problem-sheet__cell__wrapper">
            <div class="legend legend--bottom-margin">
              {{ $t('message.DEADLINE') }}
            </div>
            <DateField
              ref="pbDate"
              v-model="problem.deadline"
              :disabled="disabled"
              :placeholder="$t('message.CLICK_TO_CHANGE')"
              borderless
              @input="editProblem('deadline')"
            />
            <div v-if="originalDeadline" class="field-hint v-centerer problem-details-section__original-deadline">
              <span>{{ `${$t('message.ORIGINAL_DEADLINE_SHORT')}${$t('message.TWO_DOTS')}` }}</span>
              <span>{{ `&nbsp;${$moment(originalDeadline).format($t('dateFormat'))}` }}</span>
            </div>
          </div>
        </div>
        <div class="problem-sheet__cell">
          <div class="problem-sheet__cell__wrapper">
            <RecurrenceField
              v-if="displayRecurrenceField"
              :problem="problem"
              :disabled="disabled"
            />
          </div>
        </div>
        <div class="problem-sheet__cell">
          <div class="problem-sheet__cell__wrapper">
            <AdvancedTeamPicker
              ref="teamPicker"
              v-model="teamPickerIds"
              :quick-title="$t('message.PB_HELP_SHARE')"
              :legend="$t('message.PB_HELP_HINT')"
              display-all-teams
              ok-button
              use-editable-label
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Details section of the problem sheet
 *
 * @vue-prop {Object} problem - Problem object
 * @vue-prop {Boolean} [disabled] - Whether the problem sheet is editable
 * @vue-prop {Boolean} displayRecurrenceField - Recurrence field contains a
 *                     problem-sheet and problems-comparator components so it
 *                     might cause a cyclic redundancy
 */
import toastr from 'toastr';
import { difference } from 'lodash';
// Vuex
import { mapState } from 'vuex';
import {
  FETCH_ZONES,
  FETCH_PB_TYPES,
  FETCH_INDICATORS_ONLY,
  FETCH_KEYWORDS,
  FETCH_TEAMS,
} from '@/store/action-types';
import {
  GET_ZONES,
  GET_PB_TYPES,
  GET_KEYWORDS,
  GET_TEAMS,
} from '@/store/mutation-types';
import { EDIT_PROBLEM } from '@/store/modules/problem/action-types';
import { SET_PROBLEM_ACCESSES } from '@/store/modules/problem/mutation-types';
// Inport components
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import DateField from '@/components/date-field.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import ImpactSelector from '@/components/impact-selector.vue';
import RecurrenceField from '@/components/problem-sheet/recurrence-field.vue';
import TreeField from '@/components/tree-field.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { parseKeywords } from '@/utils/main';
import { AutocompleteChipsTypes } from '@/utils/classes/AutocompleteChipsTypes';

export default {

  name: 'ProblemDetailsSection',

  components: {
    AdvancedTeamPicker,
    DateField,
    EditableLabel,
    EditableLabelRadio,
    ImpactSelector,
    RecurrenceField,
    TreeField,
  },

  mixins: [mixinSocketHandler],

  props: {
    problem: {
      type: Object,
      required: true,
    },
    displayRecurrenceField: Boolean,
    disabled: Boolean,
  },

  data() {
    return {
      AutocompleteChipsTypes,
      isMounted: false,
    };
  },

  computed: {
    /** String: Display the hint of the currently selected imapct */
    impactHint() {
      const hint = this.currentTeam.teamProblemHints.impact.find(h => h.impact === this.problem.impact);
      return hint ? hint.text : null;
    },
    /** Array: Ids of the teams that have access to this problem */
    teamPickerIds: {
      get() {
        return [this.problem.affectTeam || 0, ...(this.problem.accesses || [])];
      },
      set(teamIds) {
        const currentAccesses = [this.problem.affectTeam, ...this.problem.accesses];
        const diffRevoke = difference(currentAccesses, teamIds);
        if (diffRevoke.length) {
          this.revokeAccess(diffRevoke);
        }
        const diffGrant = difference(teamIds, currentAccesses);
        if (diffGrant.length) {
          this.grantAccess(diffGrant);
        }
      },
    },
    /** Boolean: Is the current user allowed to edit the problem */
    accessGranted() {
      return this.currentTeam.id === Number(this.problem.affectTeam)
        || (this.problem.accesses || []).some(teamId => this.currentTeam.id === Number(teamId))
        || (this.currentUser && this.currentUser.teams.includes(Number(this.problem.affectTeam)));
    },
    /** Array: History raw data from state */
    stateHistory() {
      return this.$store.state.problem.history[`${this.problem.id}`] || [];
    },
    /** Moment: First deadline set for this problem */
    originalDeadline() {
      const firstDeadlineChange = this.stateHistory.find(hc => hc.field === 'deadline' && hc.fromValue === null);
      return firstDeadlineChange ? firstDeadlineChange.toValue : null;
    },
    // All required data
    ...mapState({
      allZones: state => state[GET_ZONES.stateKey] || [],
      allZonesLoading: state => state[GET_ZONES.loadingKey],
      allIndicators(state) {
        return (state.indicatorsArrayPerTeam[`${this.problem.affectTeam}`] || {}).data || [];
      },
      allIndicatorsLoading(state) {
        return (state.indicatorsArrayPerTeam[`${this.problem.affectTeam}`] || {}).loading;
      },
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
      allTypes: state => state[GET_PB_TYPES.stateKey] || [],
      allTypesLoading: state => state[GET_PB_TYPES.loadingKey],
      allKeywords: state => state[GET_KEYWORDS.stateKey] || [],
      allKeywordsLoading: state => state[GET_KEYWORDS.loadingKey],
    }),
  },

  watch: {
    problem(nv, ov) {
      if (nv.affectTeam !== ov.affectTeam) {
        this.$store.dispatch(FETCH_INDICATORS_ONLY, { team: nv.affectTeam });
      }
    },
  },

  created() {
    // Fetch required data (zones, types, indicators, users, keywords)
    if (!this.allZonesLoading) {
      this.$store.dispatch(FETCH_ZONES);
    }
    if (!this.allTeamsLoading) {
      this.$store.dispatch(FETCH_TEAMS);
    }
    if (!this.allTypesLoading) {
      this.$store.dispatch(FETCH_PB_TYPES);
    }
    if (!this.allIndicatorsLoading) {
      this.$store.dispatch(FETCH_INDICATORS_ONLY, { team: this.problem.affectTeam });
    }
    if (!this.allKeywordsLoading) {
      this.$store.dispatch(FETCH_KEYWORDS);
    }
  },

  mounted() {
    this.isMounted = true;
  },

  methods: {
    /**
     * When help is needed, the team needs to grant access
     * to the support or management teams
     * @param {Array} teams - List of teams to grant access to the problem
     */
    grantAccess(teams) {
      this.$http.post('/server/problems/grantAccess.php', { id: this.problem.id, teams }).then(({ data }) => {
        if (Array.isArray(data)) {
          toastr.success(this.$t('message.PB_HELP_TEAMS_NOTIFIED'));
          // Send socket updates
          data.forEach((team) => {
            this.sendSocketMessage({ msg: SocketMessages.NOTIF_MSG, team });
            this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEMS, team });
          });
          this.sendSocketMessage({
            msg: SocketMessages.CC_MSGS.PROBLEM_ACCESS,
            id: this.problem.id,
            socketCompany: 1,
          });
          // Update the state
          const accesses = data.map(({ team }) => team);
          this.$store.commit(`problem/${SET_PROBLEM_ACCESSES}`, { id: this.problem.id, accesses });
        } else if (data === -1) {
          toastr.error(this.$t('message.PB_HELP_NO_TEAM_FOUND'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /**
     * Remove problem access to a team
     * @param {Object} access - Team data object
     */
    revokeAccess(teams) {
      if (!this.accessGranted) {
        toastr.error(this.$t('message.NO_ACCESS'));
        return;
      }
      // Send API request
      this.$http.post('/server/problems/revokeAccess.php', { id: this.problem.id, teams }).then(({ data }) => {
        if (Array.isArray(data)) {
          toastr.success(this.$t('message.PB_HELP_REVOKED'));
          // Send socket updates
          data.forEach((team) => {
            this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEMS, team });
          });
          this.sendSocketMessage({
            msg: SocketMessages.CC_MSGS.PROBLEM_ACCESS,
            id: this.problem.id,
            socketCompany: 1,
          });
          this.sendSocketMessage({ msg: SocketMessages.NOTIF_MSG, team: this.problem.affectTeam });
          // Update the state
          const accesses = data.map(({ team }) => team);
          this.$store.commit(`problem/${SET_PROBLEM_ACCESSES}`, { id: this.problem.id, accesses });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Problem field was edited */
    editProblem(field, value) {
      value = value || (field === 'keywords' ? parseKeywords(this.problem.editableKeywords) : this.problem[field]);
      const params = { id: this.problem.id, [field]: value };
      this.$store.dispatch(`problem/${EDIT_PROBLEM}`, params).then(this.sendSocketUpdate);
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEM_SHEET, id: this.problem.id, socketCompany: 1 });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
.problem-details-section {
  &__original-deadline {
    margin-top: .5rem;
  }

  &__impact {
    &.problem-sheet__cell__wrapper {
      cursor: initial;
    }

    &__hint {
      margin-top: 0.3rem;
    }
  }
}
</style>
